import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

const Investors = () => {
  const [counters, setCounters] = useState({ revenue: 0, profit: 0, countries: 0, awards: 0 });
  const animated = useRef(false);

  // Scroll to philosophy section if hash is present
  useEffect(() => {
    if (window.location.hash === '#philosophy') {
      const element = document.getElementById('philosophy');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300); // delay to ensure DOM is fully rendered
      }
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            if (entry.target.classList.contains('investor-stats-grid') && !animated.current) {
              animated.current = true;
              animateNumbers();
            }
          }
        });
      },
      { threshold: 0.2 }
    );
    document.querySelectorAll('.reveal-on-scroll, .investor-stats-grid').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const animateNumbers = () => {
    const targets = { revenue: 50, profit: 20, countries: 6, awards: 12 };
    const duration = 2000;
    const stepTime = 20;
    const steps = duration / stepTime;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setCounters({
        revenue: Math.min(targets.revenue, Math.floor((targets.revenue * step) / steps)),
        profit: Math.min(targets.profit, Math.floor((targets.profit * step) / steps)),
        countries: Math.min(targets.countries, Math.floor((targets.countries * step) / steps)),
        awards: Math.min(targets.awards, Math.floor((targets.awards * step) / steps)),
      });
      if (step >= steps) clearInterval(interval);
    }, stepTime);
  };

  const performanceMetrics = [
    { label: 'Revenue Growth (YoY)', value: 24, icon: 'fas fa-chart-line', color: '#FF6B35' },
    { label: 'Market Expansion', value: 18, icon: 'fas fa-globe-asia', color: '#3B82F6' },
    { label: 'Innovation Index', value: 32, icon: 'fas fa-microchip', color: '#10B981' },
    { label: 'ESG Compliance', value: 94, icon: 'fas fa-seedling', color: '#8B5CF6' },
  ];

  const handlePhilosophyClick = (e) => {
    e.preventDefault();
    const element = document.getElementById('philosophy');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.location.hash = 'philosophy';
    }
  };

  return (
    <>
      {/* Hero Section - Enhanced */}
      <div className="investor-hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content-inner">
          <div className="hero-badge">Investor Relations</div>
          <h1 className="hero-title-main">Building Tomorrow's<br />Value Today</h1>
          <p className="hero-subtitle-main">Transparency, sustainable growth, and long-term value creation for our partners</p>
          <div className="hero-note">
            <i className="fas fa-building"></i> AALI GROUP is a privately held company – not listed on any stock exchange.
          </div>
          <div className="hero-cta-group">
            <Link to="/contact" className="btn-primary hero-btn">Connect with IR Team →</Link>
            <a href="#philosophy" className="btn-outline-light" onClick={handlePhilosophyClick}>
              Our Philosophy
            </a>
          </div>
        </div>
      </div>

      {/* Financial Highlights - Modern Card Grid */}
      <section className="investor-stats-section">
        <div className="container">
          <div className="section-header-center reveal-on-scroll">
            <span className="section-tag">Key Figures</span>
            <h2>Financial Highlights</h2>
            <p>Delivering consistent performance across our diversified portfolio</p>
          </div>
          <div className="investor-stats-grid">
            <div className="stat-card-modern reveal-on-scroll">
              <div className="stat-icon-wrapper revenue">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="stat-number">${counters.revenue}M</div>
              <div className="stat-label">Annual Revenue</div>
              <div className="stat-trend"><i className="fas fa-arrow-up"></i> +24% YoY</div>
            </div>
            <div className="stat-card-modern reveal-on-scroll">
              <div className="stat-icon-wrapper profit">
                <i className="fas fa-coins"></i>
              </div>
              <div className="stat-number">${counters.profit}M</div>
              <div className="stat-label">EBITDA</div>
              <div className="stat-trend"><i className="fas fa-arrow-up"></i> +18% YoY</div>
            </div>
            <div className="stat-card-modern reveal-on-scroll">
              <div className="stat-icon-wrapper global">
                <i className="fas fa-globe-americas"></i>
              </div>
              <div className="stat-number">{counters.countries}</div>
              <div className="stat-label">Countries Presence</div>
              <div className="stat-trend"><i className="fas fa-map-marker-alt"></i> 4 continents</div>
            </div>
            <div className="stat-card-modern reveal-on-scroll">
              <div className="stat-icon-wrapper awards">
                <i className="fas fa-medal"></i>
              </div>
              <div className="stat-number">{counters.awards}</div>
              <div className="stat-label">Industry Awards</div>
              <div className="stat-trend"><i className="fas fa-star"></i> Since 2021</div>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Metrics - Enhanced */}
      <section className="performance-section">
        <div className="container">
          <div className="section-header-center reveal-on-scroll">
            <span className="section-tag">Performance Dashboard</span>
            <h2>Key Growth Metrics</h2>
            <p>Driving excellence across every dimension of our business</p>
          </div>
          <div className="performance-grid-enhanced">
            {performanceMetrics.map((item, idx) => (
              <div key={idx} className="perf-card-enhanced reveal-on-scroll">
                <div className="perf-header">
                  <div className="perf-icon" style={{ background: `${item.color}15`, color: item.color }}>
                    <i className={item.icon}></i>
                  </div>
                  <span className="perf-value-large">{item.value}%</span>
                </div>
                <div className="perf-label">{item.label}</div>
                <div className="perf-bar-wrapper">
                  <div className="perf-bar-bg">
                    <div className="perf-bar-fill" style={{ width: `${item.value}%`, background: item.color }}></div>
                  </div>
                </div>
                <div className="perf-footer">
                  <span className="perf-target">Target: +{Math.min(item.value + 8, 100)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Philosophy - Split Layout with Quote Card */}
      <section id="philosophy" className="philosophy-section">
        <div className="container">
          <div className="philosophy-grid-enhanced">
            <div className="philosophy-text reveal-on-scroll">
              <span className="section-tag">Our Approach</span>
              <h2>Investment <span className="highlight">Philosophy</span></h2>
              <p>At AALI GROUP, we create sustainable, long-term value through strategic diversification and operational excellence. Our approach combines rigorous analysis with visionary thinking.</p>
              <div className="philosophy-features">
                <div className="philosophy-feature">
                  <div className="feature-icon"><i className="fas fa-chart-pie"></i></div>
                  <div>
                    <h4>Diversified Portfolio</h4>
                    <p>Strategic investments across high-growth sectors</p>
                  </div>
                </div>
                <div className="philosophy-feature">
                  <div className="feature-icon"><i className="fas fa-shield-alt"></i></div>
                  <div>
                    <h4>Strong Governance</h4>
                    <p>Transparency and ethical business practices</p>
                  </div>
                </div>
                <div className="philosophy-feature">
                  <div className="feature-icon"><i className="fas fa-lightbulb"></i></div>
                  <div>
                    <h4>Innovation Focus</h4>
                    <p>Continuous R&D and technology adoption</p>
                  </div>
                </div>
                <div className="philosophy-feature">
                  <div className="feature-icon"><i className="fas fa-leaf"></i></div>
                  <div>
                    <h4>ESG Commitment</h4>
                    <p>Sustainable and responsible growth</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="philosophy-quote-card reveal-on-scroll">
              <div className="quote-icon"><i className="fas fa-quote-right"></i></div>
              <p className="quote-text">We don't just build businesses – we architect futures with purpose, integrity, and a focus on lasting value.</p>
              <div className="quote-author">
                <img src="/assets/opt/ceo.webp" alt="Founder" className="author-img" />
                <div>
                  <strong>Azagar Ali Shaik</strong>
                  <span>Founder & Chairman</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - Modern Two Column */}
      <section className="investor-contact-enhanced">
        <div className="container">
          <div className="contact-container-enhanced">
            <div className="contact-left reveal-on-scroll">
              <div className="contact-badge">Connect With Us</div>
              <h2>Investor Relations</h2>
              <p>For financial inquiries, partnership opportunities, or to speak with our IR team, please reach out. We respond to all inquiries within 24 hours.</p>
              <div className="contact-details-list">
                <div className="contact-item-glass">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <strong>Email Us</strong>
                    <a href="mailto:info@aaligroup.co">info@aaligroup.co</a>
                  </div>
                </div>
                <div className="contact-item-glass">
                  <i className="fas fa-mobile-screen-button"></i>
                  <div>
                    <strong>Call Us</strong>
                    <span>+91 8341085415</span>
                  </div>
                </div>
                <div className="contact-item-glass">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <strong>Visit Us</strong>
                    <span>Guntur, Andhra Pradesh, India</span>
                  </div>
                </div>
              </div>
              <div className="contact-buttons-group">
                <Link to="/contact" className="btn-primary">Email IR Team →</Link>
                <Link to="/contact" className="btn-secondary">Request Information</Link>
              </div>
            </div>
            <div className="contact-right reveal-on-scroll">
              <div className="private-card">
                <div className="private-icon"><i className="fas fa-building"></i></div>
                <h3>Privately Held Group</h3>
                <p>AALI GROUP is a privately held conglomerate. We do not have publicly traded shares. For investment inquiries, please contact our IR team directly.</p>
                <div className="private-footer">
                  <i className="fas fa-lock"></i> 100% Private & Confidential
                </div>
              </div>
              <div className="trust-badge">
                <i className="fas fa-handshake"></i>
                <span>Trusted by Global Partners</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Investors;